import {
  Highlight,
  PayloadPutMerchflows_MerchflowId_HighlightsBasePogs,
  ResponseGetMerchflows_MerchflowId_HighlightsBasePogs,
  ResponsePutMerchflows_MerchflowId_HighlightsBasePogs,
  RouteGetMerchflows_MerchflowId_HighlightsBasePogs,
  RoutePutMerchflows_MerchflowId_HighlightsBasePogs,
} from "@CommonApi/merchflows/_merchflowId_/highlights/base-pogs";
import { getDefaultStore, useSetAtom } from "jotai";
import { baseHighlightsAtom } from "src/components/Planogram/store/atoms";
import { useToasts } from "src/components/Toasts";
import { Color } from "src/elements";
import { translationRegistry } from "src/i18n/Registry";
import type { CommonTranslations } from "src/i18n/locales/en/Common";
import { localeAtom } from "src/i18n/store/atoms";
import { insertedItemHighlightFormula } from "src/modals/Highlight/store/utils";
import { getRequest, putRequest, API } from "src/utils/axios";
import { v4 as uuid } from "uuid";

const getDefaultNewInsertedItemHighlightName = (): string => {
  const locale = getDefaultStore().get(localeAtom);
  const commonTranslations = translationRegistry[locale].common as CommonTranslations;

  return commonTranslations.highlights.defaultInsertedItemName;
};

const getDefaultNewInsertedItemHighlight = (): Highlight => ({
  id: uuid(),
  enabled: true,
  name: getDefaultNewInsertedItemHighlightName(),
  color: Color.primaryActive,
  formula: insertedItemHighlightFormula,
});

export const getDefaultBaseHighlights = (): Highlight[] => [
  {
    id: uuid(),
    enabled: true,
    name: "",
    color: "#ff0000",
    formula: "dos < minDaysOfSupply",
  },
  {
    id: uuid(),
    enabled: true,
    name: "",
    color: "#0000ff",
    formula: "mos < minOnShelf",
  },
  {
    id: uuid(),
    enabled: true,
    name: "",
    color: "#ffff00",
    formula: "newItemFlag === true",
  },
  getDefaultNewInsertedItemHighlight(),
];

export const applyBaseHighlightDefaults = (highlights: Highlight[]): Highlight[] => {
  const baseHighlights = highlights.length > 0 ? highlights : getDefaultBaseHighlights();
  const hasNewInsertedItemHighlight = baseHighlights.some(
    (highlight) => highlight.formula.trim() === insertedItemHighlightFormula,
  );

  if (hasNewInsertedItemHighlight) {
    return baseHighlights;
  }

  return [...baseHighlights, getDefaultNewInsertedItemHighlight()];
};

export const getMerchflows_MerchflowId_HighlightsBasePogsReq = (merchflowId: number) => {
  return getRequest<ResponseGetMerchflows_MerchflowId_HighlightsBasePogs>(
    `${API}${RouteGetMerchflows_MerchflowId_HighlightsBasePogs}`,
    {
      params: {
        merchflowId,
      },
    },
  );
};

export const putMerchflows_MerchflowId_HighlightsBasePogsReq = (
  merchflowId: number,
  payload: PayloadPutMerchflows_MerchflowId_HighlightsBasePogs,
) => {
  return putRequest<ResponsePutMerchflows_MerchflowId_HighlightsBasePogs>(
    `${API}${RoutePutMerchflows_MerchflowId_HighlightsBasePogs}`,
    {
      params: {
        merchflowId,
      },
      payload,
    },
  );
};

export const useHighlightsApi = () => {
  const setBaseHighlights = useSetAtom(baseHighlightsAtom);
  const { toast } = useToasts();

  const getBaseHighlightsApi = async (merchflowId: number) => {
    try {
      const { data } = await getMerchflows_MerchflowId_HighlightsBasePogsReq(merchflowId);

      // OMF-797 casualty
      setBaseHighlights(applyBaseHighlightDefaults(data));
    } catch (error) {
      toast({
        title: "Failed to load base highlights",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
      setBaseHighlights(null);
    }
  };

  const putBaseHighlightsApi = async (merchflowId: number, highlights: Highlight[]) => {
    try {
      await putMerchflows_MerchflowId_HighlightsBasePogsReq(merchflowId, highlights);
    } catch (error) {
      toast({
        title: "Failed to update base highlights",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
    }
  };

  return {
    getBaseHighlightsApi,
    putBaseHighlightsApi,
  };
};
